import { Observable } from 'rxjs';
import { getMinutesInTimestamp } from '../../lib/utils';
import { Single } from '../../lib/utils/rx-util';

const ABOUT_TO_EXPIRE_MINUTES = 10;
const EXPIRATION_EXTRA_MINUTES = 2;

const CACHE_ABOUT_TO_EXPIRE_TTL_MS = getMinutesInTimestamp(ABOUT_TO_EXPIRE_MINUTES);
const CACHE_TTL_MS = getMinutesInTimestamp(ABOUT_TO_EXPIRE_MINUTES + EXPIRATION_EXTRA_MINUTES);

class CacheEntry<T> {
  private readonly _value$ = new Single<T>();
  public readonly value$ = this._value$.asObservable();

  private expireAt: number | null = null;
  private aboutToExpireAt: number | null = null;

  public setValue(value: T) {
    if (this.isCompleted) {
      throw Error('Tried to update value of completed Single<>');
    }

    const now = new Date().getTime();
    this.expireAt = now + CACHE_TTL_MS;
    this.aboutToExpireAt = now + CACHE_ABOUT_TO_EXPIRE_TTL_MS;
    this._value$.next(value);
  }

  public get isCompleted(): boolean {
    return this._value$.hasEmitted();
  }

  public get completedValue(): T {
    if (!this.isCompleted) {
      throw Error('Tried to get completed value of a non-completed Single<>');
    }

    return this._value$.emitedValue();
  }

  public isExpired(expiryTimestamp: number): boolean {
    return this.expireAt != null && this.expireAt < expiryTimestamp;
  }

  public isAboutToExpire(): boolean {
    return this.aboutToExpireAt != null && this.aboutToExpireAt < new Date().getTime();
  }
}

export class CacheStorage<T> {
  private readonly cacheMap: Record<string, CacheEntry<T>> = {};

  public hasEntry(key: string) {
    const hasKey = key in this.cacheMap;
    const isAboutToExpire = hasKey && this.cacheMap[key].isAboutToExpire();

    if (isAboutToExpire) {
      delete this.cacheMap[key];
    }

    return hasKey && !isAboutToExpire;
  }

  public prepareEntry$(key: string): Observable<T> {
    if (this.hasEntry(key)) {
      throw Error(`Tried to override cache key ${key}`);
    }

    this.cacheMap[key] = new CacheEntry<T>();
    return this.cacheMap[key].value$;
  }

  public setCachedValue$(key: string, value: T): Observable<T> {
    if (!this.hasEntry(key)) {
      throw Error(`Tried to set cached value for non existing key ${key}`);
    }

    this.cacheMap[key].setValue(value);
    return this.cacheMap[key].value$;
  }

  public addEntryWithValue$(key: string, value: T): Observable<T> {
    if (this.hasEntry(key)) {
      throw Error(`Tried to override cache key ${key}`);
    }

    this.cacheMap[key] = new CacheEntry<T>();
    this.cacheMap[key].setValue(value);
    return this.cacheMap[key].value$;
  }

  public get$(key: string): Observable<T> {
    if (!this.hasEntry(key)) {
      throw Error(`Tried to get cached value for non existing key ${key}`);
    }

    return this.cacheMap[key].value$;
  }

  public getEntry(key: string): CacheEntry<T> {
    return this.cacheMap[key];
  }

  public isEntryCompleted(key: string): boolean {
    if (!this.hasEntry(key)) {
      throw Error(`Tried to read cached entry for non existing key ${key}`);
    }

    return this.cacheMap[key].isCompleted;
  }

  public getNonCompletedKeys(): string[] {
    return Object.keys(this.cacheMap).filter((key) => !this.cacheMap[key].isCompleted);
  }

  public cleanupExpiredCache(expiryTimestamp: number) {
    const expiredKeys: string[] = [];

    for (const key of Object.keys(this.cacheMap)) {
      if (this.cacheMap[key].isExpired(expiryTimestamp)) {
        expiredKeys.push(key);
      }
    }

    for (const key of expiredKeys) {
      delete this.cacheMap[key];
    }
  }

  public getCacheSnapshot(): Record<string, T> {
    const ret: Record<string, T> = {};

    for (const key of Object.keys(this.cacheMap)) {
      if (this.hasEntry(key) && this.cacheMap[key].isCompleted) {
        ret[key] = this.cacheMap[key].completedValue;
      }
    }

    return ret;
  }

  public restoreFromCacheSnapshot(snapshot: Record<string, T>) {
    for (const key of Object.keys(snapshot)) {
      try {
        this.addEntryWithValue$(key, snapshot[key]);
      } catch (e) {
        console.error(e);
      }
    }
  }
}
